import { useMemo } from 'react';
import { HallMapViewer, type MapTable } from '@lmac/ui';
import {
  clubDate,
  formatCents,
  type PublicMapChunk,
  type SwapMeetEvent,
} from '@lmac/shared';

/**
 * The compact view for the club's own page.
 *
 * Its job is to answer, at a glance and without any commitment, the question
 * the club fields most often before a swap meet: are there tables left, and
 * which ones. Last year's hall sold out, so "how full is it" is genuinely
 * what people want to know.
 *
 * Read-only by construction — no `onToggle` reaches the map, so nothing is
 * clickable and no hold can be created by accident. Reserving is a deliberate
 * second step.
 */
export interface PreviewProps {
  event: SwapMeetEvent;
  map: PublicMapChunk;
  onStart: () => void;
}

export function Preview({ event, map, onStart }: PreviewProps) {
  const tables: MapTable[] = useMemo(
    () => map.tables.map((t) => ({
      id: t.id, label: t.label, x: t.x, y: t.y, w: t.w, h: t.h,
      status: t.status, nearPower: t.nearPower,
    })),
    [map.tables],
  );

  const sellable = tables.filter((t) => t.status !== 'blocked');
  const free = sellable.filter((t) => t.status === 'available');
  const taken = sellable.length - free.length;
  const freePowered = free.filter((t) => t.nearPower).length;
  const pctTaken = sellable.length ? Math.round((taken / sellable.length) * 100) : 0;
  const soldOut = free.length === 0;

  return (
    <section className="pv">
      <header className="pv__head">
        <p className="pv__eyebrow">Lexington Model Airplane Club</p>
        <h1 className="pv__title">{event.name}</h1>
        <p className="pv__when">
          {event.dateLabel} &middot; {event.venueAddress}
        </p>
      </header>

      <div className="pv__stats">
        <div className="pv__stat">
          <span className="pv__stat-n">{free.length}</span>
          <span className="pv__stat-l">
            {/* "91 of 91 tables left" is technically true and reads as a
                mistake. Name the denominator only once it means something. */}
            {soldOut
              ? 'tables left — the hall is full'
              : taken === 0
                ? 'tables available'
                : `of ${sellable.length} tables left`}
          </span>
        </div>
        <div className="pv__stat">
          <span className="pv__stat-n">{formatCents(event.pricePerTable)}</span>
          <span className="pv__stat-l">
            in advance &middot; {formatCents(event.dayOfPricePerTable)} on the day
          </span>
        </div>
        {freePowered > 0 && (
          <div className="pv__stat">
            <span className="pv__stat-n">{freePowered}</span>
            <span className="pv__stat-l">still free near power</span>
          </div>
        )}
      </div>

      {/* A filled bar reads faster than a number when the question is
          "is it nearly full". Hidden when nothing is taken yet, where it
          would only draw attention to an empty hall. */}
      {taken > 0 && (
        <div className="pv__bar" role="img"
             aria-label={`${taken} of ${sellable.length} tables reserved`}>
          <span className="pv__bar-fill" style={{ width: `${pctTaken}%` }} />
          <span className="pv__bar-text">{pctTaken}% reserved</span>
        </div>
      )}

      <HallMapViewer
        caption={`${event.venueName} — current availability`}
        tables={tables}
        width={map.map.width}
        height={map.map.height}
        content={map.content}
        background={map.background}
        outlets={map.outlets}
        checkInTables={map.checkInTables}
      />

      <div className="mapkey">
          <span className="mapkey__title">Key</span>
        <span><i className="sw sw--free" />Available</span>
        <span><i className="sw sw--pending" />Pending</span>
        <span><i className="sw sw--gone" />Reserved</span>
        <span><i className="sw sw--off" />Club table</span>
        <span><i className="sw sw--power" />Power nearby</span>
        <span><i className="sw sw--checkin" />Check-in</span>
      </div>

      {soldOut ? (
        <div className="pv__cta pv__cta--out">
          <p>
            <b>Every table is reserved.</b> Email{' '}
            <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a> to
            join the waiting list in case one is released.
          </p>
        </div>
      ) : (
        <div className="pv__cta">
          <button className="pv__btn" onClick={onStart}>
            Make your reservation now
          </button>
          <p className="pv__cta-note">
            Choose your own tables and pay by card or PayPal.{' '}
            {formatCents(event.pricePerTable)} a table until{' '}
            {clubDate(event.reservationsCloseAt)}
            , {formatCents(event.dayOfPricePerTable)} on the day.
          </p>
        </div>
      )}
    </section>
  );
}
